import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faExchangeAlt,
  faWallet,
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const CorporateList = ({ searchTerm }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    corp_code: "",
    corp_name: "",
    effective_date: "",
    expiry_date: "",
    status_flag: "",
  });

  // Placeholder data based on corporate table structure
  const data = Array.from({ length: 1211 }, (_, index) => ({
    tbl_id: index + 1,
    corp_code: `CORP-${String(index + 1).padStart(4, "0")}`,
    corp_name: `Corporate ${String.fromCharCode(65 + (index % 26))}`,
    effective_date: `2023-01-${String(index % 30 + 1).padStart(2, "0")}`,
    expiry_date: `2024-12-${String(index % 30 + 1).padStart(2, "0")}`,
    status_flag: (index % 4) + 1, // Cycles through 1 to 4 (Active, Terminated, Suspended, Expired)
  }));

  // Status mapping (temporary until integrated with corporate_status table)
  const statusMap = {
    1: { name: "Active", color: "bg-green-100 text-green-700" },
    2: { name: "Terminated/Cancelled", color: "bg-red-100 text-red-700" },
    3: { name: "Suspended", color: "bg-yellow-100 text-yellow-700" },
    4: { name: "Expired", color: "bg-gray-100 text-gray-700" },
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(data.length / itemsPerPage);

  // Apply search and filters
  const filteredData = data.filter((item) => {
    const matchesSearch = Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesFilters = Object.keys(filters).every((key) =>
      filters[key] === "" ||
      item[key].toString().toLowerCase().includes(filters[key].toLowerCase())
    );
    return matchesSearch && matchesFilters;
  });

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFilterChange = (e, key) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  };

  const renderPaginationNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { key: "corp_code", label: "Corporate Code" },
          { key: "corp_name", label: "Corporate Name" },
          { key: "effective_date", label: "Effective Date" },
          { key: "expiry_date", label: "Expiry Date" },
          { key: "status_flag", label: "Status Flag" },
        ].map(({ key, label }) => (
          <div key={key} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">{label}</label>
            {key === "status_flag" ? (
              <select
                value={filters[key]}
                onChange={(e) => handleFilterChange(e, key)}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Statuses</option>
                {Object.entries(statusMap).map(([code, { name }]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            ) : (
              <input
                type={key.includes("date") ? "date" : "text"}
                placeholder={`Filter by ${label}`}
                value={filters[key]}
                onChange={(e) => handleFilterChange(e, key)}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            )}
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3">Corporate Code</th>
              <th scope="col" className="px-6 py-3">Corporate Name</th>
              <th scope="col" className="px-6 py-3">Effective Date</th>
              <th scope="col" className="px-6 py-3">Expiry Date</th>
              <th scope="col" className="px-6 py-3">Status Flag</th>
              <th scope="col" className="px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <tr
                  key={item.tbl_id}
                  className="bg-white border-b hover:bg-gray-50 transition-all duration-200"
                >
                  <td className="px-6 py-4 font-medium">{item.corp_code}</td>
                  <td className="px-6 py-4">{item.corp_name}</td>
                  <td className="px-6 py-4">{item.effective_date}</td>
                  <td className="px-6 py-4">{item.expiry_date}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusMap[item.status_flag]?.color || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {statusMap[item.status_flag]?.name || "Unknown"}
                    </span>
                  </td>
                  <td className="px-6 py-4 flex gap-2 flex-wrap">
                    <button className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-all duration-200 text-xs font-medium">
                      <FontAwesomeIcon icon={faEye} className="mr-1" /> View
                    </button>
                    <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-all duration-200 text-xs font-medium">
                      <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                    </button>
                    <button className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-all duration-200 text-xs font-medium">
                      <FontAwesomeIcon icon={faExchangeAlt} className="mr-1" /> Movement
                    </button>
                    <button className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-all duration-200 text-xs font-medium">
                      <FontAwesomeIcon icon={faWallet} className="mr-1" /> Add Fund
                    </button>
                    <button className="px-3 py-1 bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200 transition-all duration-200 text-xs font-medium">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Finalize Corporate
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No corporates found matching the filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination and Showing Entries */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-500">
          Showing{" "}
          {filteredData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}{" "}
          to{" "}
          {Math.min(filteredData.length, currentPage * itemsPerPage)} of{" "}
          {filteredData.length} entries
        </div>
        <div className="flex items-center">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {renderPaginationNumbers()}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`px-3 py-1 mx-1 rounded-md text-sm font-medium transition-all duration-200 ${
              currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorporateList;